// Delivery → My shops: the company's own shops, where a buyer may collect an order when the company
// offers "Pickup from the shop" (its shipping providers). Each shop: a name, its town and address,
// a phone, opening hours, a note for the buyer, and its place on the map (a pin, found from the
// address or put by hand) — what the checkout's list, "near me" and map show. Saved with the
// rest of the delivery (profile_save `shops`).
import React, { useEffect, useRef, useState } from "react"
import "leaflet/dist/leaflet.css"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { BuildingStorefrontIcon, MapPinIcon, PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Field, TextInput, TextArea, Section, Grid, Empty } from "../AdminShared/ui"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { COMPANY } from "../Vendor/texts/bundles"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const SOFIA = { lat: 42.6977, lng: 23.3219 }
const PIN = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40"><path d="M15 0C6.7 0 0 6.6 0 14.8 0 25.9 15 40 15 40s15-14.1 15-25.2C30 6.6 23.3 0 15 0z" fill="#e2533a"/><circle cx="15" cy="14.5" r="5.5" fill="#fff"/></svg>`
const hasPoint = (shop) => Number.isFinite(Number(shop?.lat)) && Number.isFinite(Number(shop?.lng)) && shop.lat !== null && shop.lng !== null && shop.lat !== ""
const EMPTY = { name: "", city: "", address: "", postCode: "", phone: "", hours: "", note: "", active: true }

/** Where an address is on the map (OpenStreetMap's Nominatim): { lat, lng } or null. */
async function geocode({ address, city, postCode }) {
  const q = [address, postCode, city, "Bulgaria"].filter(Boolean).join(", ").replace(/^, /, "")
  const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=bg&q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } })
  const found = (await res.json())?.[0]
  return found ? { lat: Math.round(Number(found.lat) * 1e6) / 1e6, lng: Math.round(Number(found.lon) * 1e6) / 1e6 } : null
}

/**
 * A map of shops (Leaflet). `editable`: one pin, put by a click and moved by dragging — `onMove({ lat, lng })`.
 */
// `focus`: a place to show with no pin on it (the shop's town, when its address was not found — to pin it there)
function ShopsMap({ shops, editable = false, onMove, focus = null, className = "h-[260px]" }) {
  const box = useRef(null)
  const state = useRef({ map: null, L: null, layer: null })
  const moveRef = useRef(onMove)
  moveRef.current = onMove

  useEffect(() => {
    let alive = true
    const s = state.current
    ;(async () => {
      const L = (await import("leaflet")).default
      if (!alive || !box.current) return
      s.L = L
      const map = L.map(box.current, { zoomControl: true }).setView([SOFIA.lat, SOFIA.lng], 7)
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19, attribution: "&copy; OpenStreetMap" }).addTo(map)
      s.map = map
      s.layer = L.layerGroup().addTo(map)
      if (editable) map.on("click", (e) => moveRef.current?.({ lat: Math.round(e.latlng.lat * 1e6) / 1e6, lng: Math.round(e.latlng.lng * 1e6) / 1e6 }))
      draw()
    })()
    return () => {
      alive = false
      s.map?.remove()
      s.map = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const draw = () => {
    const { map, L, layer } = state.current
    if (!map || !L) return
    layer.clearLayers()
    const placed = shops.filter(hasPoint)
    for (const shop of placed) {
      const marker = L.marker([Number(shop.lat), Number(shop.lng)], { icon: L.divIcon({ html: PIN, className: "", iconSize: [30, 40], iconAnchor: [15, 40], popupAnchor: [0, -36] }), draggable: editable, title: shop.name || "" })
      if (editable) marker.on("dragend", () => {
        const p = marker.getLatLng()
        moveRef.current?.({ lat: Math.round(p.lat * 1e6) / 1e6, lng: Math.round(p.lng * 1e6) / 1e6 })
      })
      else marker.bindPopup(`<b>${String(shop.name || "").replace(/</g, "&lt;")}</b><br>${String([shop.address, shop.city].filter(Boolean).join(", ")).replace(/</g, "&lt;")}`)
      marker.addTo(layer)
    }
    if (placed.length === 1) map.setView([Number(placed[0].lat), Number(placed[0].lng)], editable ? 16 : 14)
    else if (placed.length > 1) map.fitBounds(L.latLngBounds(placed.map((shop) => [Number(shop.lat), Number(shop.lng)])).pad(0.2))
  }
  useEffect(draw, [shops]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (focus && state.current.map) state.current.map.setView([focus.lat, focus.lng], 14)
  }, [focus])

  return <div ref={box} className={`w-full rounded-md border border-admin_bordered overflow-hidden z-0 ${className}`} data-testid={editable ? "shop-pin-map" : "shops-map"} />
}

/** Add or edit one shop. */
function ShopDialog({ shop, onClose, onSave }) {
  const t = usePortalTexts(COMPANY)
  const [draft, setDraft] = useState({ ...EMPTY, ...shop })
  const [finding, setFinding] = useState(false)
  const [notFound, setNotFound] = useState(false)
  // the address not found: the map goes to the town, where the vendor pins the shop
  const [focus, setFocus] = useState(null)
  const [tried, setTried] = useState(false)
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }))
  const missing = { name: !draft.name.trim(), city: !draft.city.trim(), address: !draft.address.trim() }
  const find = async () => {
    setFinding(true)
    setNotFound(false)
    const point = await geocode(draft).catch(() => null)
    if (!point) {
      const town = await geocode({ city: draft.city }).catch(() => null)
      setFinding(false)
      setNotFound(true)
      if (town) setFocus(town)
      return
    }
    setFinding(false)
    set(point)
  }
  const save = () => {
    setTried(true)
    if (missing.name || missing.city || missing.address) return
    onSave(draft)
  }
  return (
    <Modal
      isOpen
      onClose={onClose}
      variant="admin_centered"
      className="max-w-2xl"
      title={shop?.id ? t`Edit the shop` : t`Add a shop`}
      closeOnEscape
      footerContent={
        <div className="flex justify-end gap-2">
          <Button variant="admin_secondary" onClick={onClose}>{t`Cancel`}</Button>
          <Button variant="admin_primary" onClick={save} data-testid="shop-save">{t`Save`}</Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="shop-dialog">
        <Grid cols={2}>
          <Field label={t`Shop name`} error={tried && missing.name ? t`Required` : null}><TextInput value={draft.name} onChange={(v) => set({ name: v })} placeholder={t`e.g. Shop Vitosha`} data-testid="shop-name" /></Field>
          <Field label={t`Town`} error={tried && missing.city ? t`Required` : null}><TextInput value={draft.city} onChange={(v) => set({ city: v })} data-testid="shop-city" /></Field>
          <Field label={t`Address`} error={tried && missing.address ? t`Required` : null}><TextInput value={draft.address} onChange={(v) => set({ address: v })} placeholder={t`Street and number`} data-testid="shop-address" /></Field>
          <Field label={t`Post code`}><TextInput value={draft.postCode} onChange={(v) => set({ postCode: v })} data-testid="shop-postcode" /></Field>
          <Field label={t`Phone`}><TextInput value={draft.phone} onChange={(v) => set({ phone: v })} data-testid="shop-phone" /></Field>
          <Field label={t`Opening hours`}><TextInput value={draft.hours} onChange={(v) => set({ hours: v })} placeholder={t`e.g. Mon–Fri 10:00–19:00, Sat 10:00–14:00`} data-testid="shop-hours" /></Field>
        </Grid>
        <Field label={t`Note for the buyer`} hint={t`Optional: how to find the shop, where to park, what to bring.`}>
          <TextArea value={draft.note} onChange={(v) => set({ note: v })} rows={2} maxLength={500} data-testid="shop-note" />
        </Field>
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-medium text-admin_text_secondary">{t`Place on the map`}</span>
            <Button variant="admin_secondary" className="flex items-center" onClick={find} disabled={finding || !draft.address.trim() || !draft.city.trim()} loading={finding} data-testid="shop-find">
              <MapPinIcon className="w-4 h-4 mr-1" />
              {t`Find the address on the map`}
            </Button>
          </div>
          <ShopsMap shops={hasPoint(draft) ? [draft] : []} editable focus={focus} onMove={(point) => (set(point), setNotFound(false))} />
          <span className={`text-[11px] ${notFound ? "text-admin_error" : "text-admin_text_secondary"}`}>
            {notFound ? t`The address was not found on the map: click the map where the shop is to pin it.` : hasPoint(draft) ? t`Drag the pin to the shop's door if it is not exact.` : t`Find the address, or click the map where the shop is. Buyers see it on the map and by distance ("near me").`}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <ToggleSwitch variant="admin" isOn={draft.active !== false} onChange={(on) => set({ active: !!on })} />
          <span className="text-sm text-admin_text">{draft.active !== false ? t`Open for pickups` : t`Closed for pickups (not shown to buyers)`}</span>
        </div>
      </div>
    </Modal>
  )
}

/**
 * The company's shops: `value` the list as saved, `onChange(shops, { now: true })` saves it.
 * `offered`: whether "Pickup from the shop" is on in its providers (said under the list when it is not).
 */
export default function ShopsSection({ value, canEdit, onChange, offered = false }) {
  const t = usePortalTexts(COMPANY)
  const shops = Array.isArray(value) ? value : []
  const [editing, setEditing] = useState(null)
  const save = (shop) => {
    const next = shop.id ? shops.map((s) => (s.id === shop.id ? shop : s)) : [...shops, shop]
    onChange(next, { now: true })
    setEditing(null)
  }
  const remove = (shop) =>
    showPromptDialog({
      variant: "admin",
      title: t`Remove this shop?`,
      message: t("%s is no longer offered for pickups. Orders already placed keep it.", shop.name),
      confirmText: t`Remove`,
      cancelText: t`Cancel`,
      onConfirm: () => onChange(shops.filter((s) => s.id !== shop.id), { now: true }),
    })
  const toggle = (shop, on) => onChange(shops.map((s) => (s.id === shop.id ? { ...s, active: on } : s)), { now: true })

  return (
    <Section
      title={<span className="inline-flex items-center gap-1.5">{t`My shops`}<HelpHint helpKey="vendor.delivery#shops" /></span>}
      subtitle={t`Where buyers can collect their orders, when you offer "Pickup from the shop" in your shipping providers`}
      actions={canEdit && (
        <Button variant="admin_primary" onClick={() => setEditing({})} data-testid="shop-add">
          {t`Add a shop`}
        </Button>
      )}
    >
      <div className="flex flex-col gap-3" data-testid="shops">
        {!shops.length ? (
          <Empty>{t`No shops yet. Add the shops where buyers can collect their orders.`}</Empty>
        ) : (
          shops.map((shop) => (
            <div key={shop.id || shop.name} className="flex items-start gap-3 rounded-md border border-admin_bordered p-4" data-testid="shop-row">
              <BuildingStorefrontIcon className="w-6 h-6 shrink-0 text-admin_text_secondary mt-0.5" />
              <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-admin_text">{shop.name}</span>
                  <span className={`text-xs ${shop.active !== false ? "text-admin_success font-semibold" : "text-admin_text_secondary"}`}>{shop.active !== false ? t`Open for pickups` : t`Closed`}</span>
                  {!hasPoint(shop) && <span className="text-xs text-admin_warning">{t`Not on the map`}</span>}
                </div>
                <span className="text-xs text-admin_text_secondary">{[shop.address, shop.postCode, shop.city].filter(Boolean).join(", ")}</span>
                {(shop.phone || shop.hours) && <span className="text-xs text-admin_text_secondary">{[shop.phone, shop.hours].filter(Boolean).join(" · ")}</span>}
              </div>
              {canEdit && (
                <div className="flex items-center gap-2 shrink-0">
                  <ToggleSwitch variant="admin" isOn={shop.active !== false} onChange={(on) => toggle(shop, !!on)} />
                  <button type="button" className="p-1.5 rounded hover:bg-admin_neutral_1" onClick={() => setEditing(shop)} aria-label={t`Edit`} data-testid="shop-edit">
                    <PencilSquareIcon className="w-5 h-5 text-admin_text_secondary" />
                  </button>
                  <button type="button" className="p-1.5 rounded hover:bg-admin_neutral_1" onClick={() => remove(shop)} aria-label={t`Remove`} data-testid="shop-remove">
                    <TrashIcon className="w-5 h-5 text-admin_text_secondary" />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
        {shops.some(hasPoint) && <ShopsMap shops={shops.filter((s) => s.active !== false)} />}
        {shops.length > 0 && !offered && <div className="text-xs text-admin_text_secondary" data-testid="shops-not-offered">{t`Buyers can pick these shops once "Pickup from the shop" is on in Shipping providers.`}</div>}
      </div>
      {editing && <ShopDialog shop={editing} onClose={() => setEditing(null)} onSave={save} />}
    </Section>
  )
}
